import React from 'react';
import { Plus, X, AlertTriangle, User, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { generateId, formatDuration } from '../utils/projectUtils';

const PhaseSection = ({ 
  phaseKey, 
  phaseName, 
  phaseColor, 
  formData, 
  setFormData, 
  calculatedDates, 
  personnesData = [], 
  errors = {} 
}) => {
  const phase = formData.phases?.[phaseKey] || { affectations: [] };
  const phaseCalc = calculatedDates[phaseKey];
  const budgetField = `budget_${phaseKey}`;

  const addAffectation = () => {
    setFormData(prev => ({
      ...prev,
      phases: {
        ...prev.phases,
        [phaseKey]: {
          ...prev.phases[phaseKey],
          affectations: [
            ...(prev.phases[phaseKey]?.affectations || []),
            {
              id: generateId(),
              id_personne: '',
              pourcentage_temps: 100,
              role_projet: 'developpeur'
            }
          ]
        }
      }
    }));
  };
  
  const updateAffectation = (affectationId, field, value) => {
    setFormData(prev => ({
      ...prev,
      phases: {
        ...prev.phases,
        [phaseKey]: {
          ...prev.phases[phaseKey],
          affectations: (prev.phases[phaseKey]?.affectations || []).map(aff =>
            aff.id === affectationId ? { ...aff, [field]: value } : aff
          )
        }
      }
    }));
  };
  
  const removeAffectation = (affectationId) => {
    setFormData(prev => ({
      ...prev,
      phases: {
        ...prev.phases,
        [phaseKey]: {
          ...prev.phases[phaseKey],
          affectations: (prev.phases[phaseKey]?.affectations || []).filter(aff => aff.id !== affectationId)
        }
      }
    }));
  };

  const getPersonneInfo = (personneId) => {
    return personnesData.find(p => p.id === parseInt(personneId));
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-900',
        textLight: 'text-blue-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-900',
        textLight: 'text-green-700'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-900',
        textLight: 'text-purple-700'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const colors = getColorClasses(phaseColor);
  
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
      {/* En-tête de la phase */}
      <div className="flex items-center justify-between mb-4">
        <h4 className={`text-sm font-medium ${colors.text} flex items-center`}>
          <div className={`w-3 h-3 bg-${phaseColor}-500 rounded-sm mr-2`}></div>
          Phase {phaseName}
        </h4>
        
        {phaseCalc && (
          <div className="text-right">
            <div className="text-xs text-gray-600">
              {phaseCalc.date_debut && format(phaseCalc.date_debut, 'dd/MM/yyyy', { locale: fr })} → {' '}
              {phaseCalc.date_fin && format(phaseCalc.date_fin, 'dd/MM/yyyy', { locale: fr })}
            </div>
            <div className={`text-sm font-medium flex items-center justify-end ${
              phaseCalc.surcharge ? 'text-red-600' : colors.textLight
            }`}>
              <Clock className="h-3 w-3 mr-1" />
              {formatDuration(phaseCalc.duree_semaines)}
              {phaseCalc.surcharge && <AlertTriangle className="h-3 w-3 ml-1 text-red-500" />}
            </div>
          </div>
        )}
      </div>
      
      {/* Budget et capacité */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Budget planifié (jours) *
          </label>
          <input
            type="number"
            value={formData[budgetField] || ''}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                [budgetField]: e.target.value,
                phases: {
                  ...prev.phases,
                  [phaseKey]: {
                    ...prev.phases[phaseKey],
                    budget_jours: parseFloat(e.target.value) || 0
                  }
                }
              }));
            }}
            className={`form-input ${errors[budgetField] ? 'border-red-500' : ''}`}
            placeholder="15"
            min="0"
            step="0.5"
          />
          {errors[budgetField] && (
            <p className="mt-1 text-sm text-red-600">{errors[budgetField]}</p>
          )}
        </div>
        
        {phaseCalc && (
          <>
            <div className="flex items-end">
              <div className="text-sm">
                <div className="text-gray-600">Capacité équipe:</div>
                <div className="font-medium text-gray-900 flex items-center">
                  <User className="h-3 w-3 mr-1" />
                  {phaseCalc.capacite_equipe.toFixed(1)} j/semaine
                </div>
              </div>
            </div>
            
            <div className="flex items-end">
              <div className="text-sm">
                <div className="text-gray-600">Personnes affectées:</div>
                <div className="font-medium text-gray-900">
                  {phaseCalc.affectations_valides} / {phase.affectations?.length || 0}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Affectations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Personnes affectées
          </label>
          <button
            type="button"
            onClick={addAffectation}
            className="text-xs bg-white border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 flex items-center"
          >
            <Plus className="h-3 w-3 mr-1" />
            Ajouter
          </button>
        </div>
        
        {phase.affectations?.map((affectation) => {
          const personne = getPersonneInfo(affectation.id_personne);
          return (
            <div key={affectation.id} className="flex items-center space-x-3 bg-white p-3 rounded border">
              <select
                value={affectation.id_personne}
                onChange={(e) => updateAffectation(affectation.id, 'id_personne', e.target.value)}
                className="form-select flex-1"
              >
                <option value="">Sélectionner une personne</option>
                {personnesData?.filter(p => p.actif).map(personne => (
                  <option key={personne.id} value={personne.id}>
                    {personne.prenom} {personne.nom} 
                    (Prod: {Math.round((personne.indice_productivite || 0) * 100)}%)
                  </option>
                ))}
              </select>
              
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={affectation.pourcentage_temps}
                  onChange={(e) => updateAffectation(affectation.id, 'pourcentage_temps', e.target.value)}
                  className="form-input w-16 text-sm"
                  title="Pourcentage du temps de la personne"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
              
              <select
                value={affectation.role_projet}
                onChange={(e) => updateAffectation(affectation.id, 'role_projet', e.target.value)}
                className="form-select w-32 text-sm"
                title="Rôle dans le projet"
              >
                <option value="developpeur">Développeur</option>
                <option value="chef_projet">Chef de projet</option>
                <option value="designer">Designer</option>
                <option value="testeur">Testeur</option>
                <option value="analyste">Analyste</option>
              </select>
              
              {personne && (
                <div className="text-xs text-gray-500 min-w-0 flex-shrink-0">
                  {(5 * (personne.indice_productivite || 0) * (affectation.pourcentage_temps || 100) / 100).toFixed(1)}j/sem
                </div>
              )}
              
              <button
                type="button"
                onClick={() => removeAffectation(affectation.id)}
                className="text-red-600 hover:text-red-700 p-1 flex-shrink-0"
                title="Supprimer cette affectation"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
        
        {(!phase.affectations || phase.affectations.length === 0) && (
          <div className="text-center py-4 text-gray-500 text-sm italic border-2 border-dashed border-gray-200 rounded">
            Aucune personne affectée à cette phase
            <br />
            <button
              type="button"
              onClick={addAffectation}
              className="text-primary-600 hover:text-primary-700 underline mt-1"
            >
              Ajouter la première affectation
            </button>
          </div>
        )}
      </div>

      {/* Warnings et erreurs */}
      {phaseCalc?.warnings?.length > 0 && (
        <div className="mt-3 space-y-1">
          {phaseCalc.warnings.map((warning, index) => (
            <div key={index} className="flex items-center text-sm text-orange-600 bg-orange-50 p-2 rounded">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              {warning}
            </div>
          ))}
        </div>
      )}

      {phaseCalc?.surcharge && (
        <div className="mt-3">
          <div className="flex items-center text-sm text-red-600 bg-red-50 p-2 rounded">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
            Cette phase nécessite {phaseCalc.budget_jours} jours mais aucune personne n'est affectée
          </div>
        </div>
      )}
    </div>
  );
};

export default PhaseSection;
